import {
  KeyRoundIcon,
  PauseIcon,
  PlayIcon,
  PlugZapIcon,
  RadioIcon,
  RefreshCwIcon,
  ShieldIcon,
  UserMinusIcon,
} from 'lucide-react'
import { ApiErrorText } from '@/components/UpgradePrompt'
import { useEffect, useState, type FormEvent } from 'react'

import { errorMessage } from '@/api/client'
import {
  CONNECTIONS_POLL_MS,
  useConnections,
  useDisconnectConnection,
  useIsZoneAdministrator,
  useMe,
  usePauseConnection,
  useReissueConnection,
  useReleaseConnection,
  useUnpauseConnection,
  useUsers,
} from '@/api/queries'
import type {
  ConnectionCounters,
  ConnectionSummary,
  HeldMessageDisposition,
  ReissueResponse,
  ReleaseResponse,
  UnpauseResponse,
  ZoneSummary,
} from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { CopyableValue } from '@/components/CodeBlock'
import { ConnectionStateBadge, ConnectionTypeBadge, PausedBadge } from '@/components/badges'
import { PausedZoneNotice, RequireZone } from '@/components/RequireZone'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { formatAbsolute, formatDurationSince, formatRelative, pluralise } from '@/lib/format'
import { cn } from '@/lib/utils'

import { claimedLabel, incarnationTag } from './incarnation'
import { useReadOnly } from '@/staff/hooks'

/** Re-renders relative timestamps on the same cadence as the poll. */

/**
 * Protocol 7, A4: the identity's waste counters. A streak of 10 empty waits is the point at which
 * the server starts telling the agent to use the watcher, so it is the point we colour.
 */
function CountersCell({ counters }: { counters: ConnectionCounters | undefined }) {
  if (counters === undefined) {
    return <span>&mdash;</span>
  }
  const hot = counters.empty_wait_streak >= 10
  return (
    <div
      className="flex flex-col gap-0.5 font-mono tabular"
      title="Empty waits (total, and in a row), receives that overlapped a running one, reclaims by id"
    >
      <span className={hot ? 'font-medium text-destructive' : undefined}>
        {counters.empty_waits} empty ({counters.empty_wait_streak} in a row)
      </span>
      <span>
        {counters.duplicate_waits} dup &middot; {counters.reconnects} reconnects
      </span>
    </div>
  )
}

function useTicker(intervalMs: number): number {
  const [tick, setTick] = useState(() => Date.now())
  useEffect(() => {
    const handle = window.setInterval(() => {
      setTick(Date.now())
    }, intervalMs)
    return () => {
      window.clearInterval(handle)
    }
  }, [intervalMs])
  return tick
}

function LiveIndicator({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
      <span
        className={cn(
          'size-1.5 rounded-full',
          active ? 'animate-pulse bg-[var(--accent)]' : 'bg-muted-foreground/50',
        )}
      />
      Live · refreshing every {Math.round(CONNECTIONS_POLL_MS / 1000)}s
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Who paused it                                                              */
/* -------------------------------------------------------------------------- */

/**
 * §19 records the actor as *ids* — the server refuses to walk the lazy relations
 * during serialisation (bug-010) — so the console resolves them itself, against
 * data it already holds: the connection list for the police, `/api/users` for
 * the person. An id it cannot resolve is shown as an id rather than swallowed;
 * "paused by somebody" is a worse answer than an unfamiliar identifier.
 */
function actorLabel(
  connection: ConnectionSummary,
  byName: Map<string, string>,
  userNames: Map<string, string>,
  myUserId: string | undefined,
): string {
  const police = connection.paused_by_connection_id
  if (police !== null) {
    return byName.get(police) ?? `connection ${police.slice(0, 8)}`
  }
  const user = connection.paused_by_user_id
  if (user === null) return 'the platform'
  if (user === myUserId) return 'you'
  return userNames.get(user) ?? `user ${user.slice(0, 8)}`
}

/* -------------------------------------------------------------------------- */
/* Pause                                                                      */
/* -------------------------------------------------------------------------- */

function PauseDialog({
  zone,
  target,
  onClose,
}: {
  zone: ZoneSummary
  target: ConnectionSummary | undefined
  onClose: () => void
}) {
  const pause = usePauseConnection()
  const [reason, setReason] = useState('')

  function close() {
    setReason('')
    pause.reset()
    onClose()
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (target === undefined) return
    const stated = reason.trim()
    if (stated.length === 0) return
    pause.mutate({ zoneId: zone.id, id: target.id, reason: stated }, { onSuccess: close })
  }

  return (
    <Dialog
      open={target !== undefined}
      onOpenChange={(next) => {
        if (!next) close()
      }}
    >
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              Pause <span className="font-mono">{target?.name ?? 'this connection'}</span>?
            </DialogTitle>
            <DialogDescription>
              A pause is not a disconnect. The agent stays connected and its tools keep answering —
              what stops is delivery, in both directions. Anything addressed to it is held rather
              than delivered, and anything it sends is accepted and reaches nobody. Nothing is
              lost; you decide what happens to both backlogs when you lift the pause.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-5">
            <div className="space-y-1.5">
              <Label htmlFor="pause-reason">Reason</Label>
              <Textarea
                id="pause-reason"
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
                placeholder="Flooding the zone with retries"
                rows={3}
                maxLength={512}
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Required, and it is not just a note for you. It is delivered verbatim to the agent
                in the order to cease operations, and it is emailed to this zone&apos;s
                administrator.
              </p>
            </div>
            {pause.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Could not pause that connection</AlertTitle>
                <AlertDescription>{errorMessage(pause.error)}</AlertDescription>
              </Alert>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={pause.isPending}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={pause.isPending || reason.trim().length === 0}
            >
              {pause.isPending ? 'Pausing…' : 'Pause and notify'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/* Unpause — two independent choices (§19)                                    */
/* -------------------------------------------------------------------------- */

interface DispositionCopy {
  value: HeldMessageDisposition
  label: string
  detail: string
}

/**
 * §19 gives the administrator one choice **per direction** and the console must
 * not collapse them: releasing what an agent *said* while paused is a very
 * different decision from releasing what it was *told*. There is deliberately
 * no "same for both" shortcut, and neither side is preselected in a way that
 * lets the dialog be dismissed without both being considered.
 *
 * The labels are stated as consequences, not as the wire enum: an administrator
 * choosing between `DELIVER` and `DISCARD` is choosing whether a possibly
 * misbehaving agent's backlog reaches other agents.
 */
const INBOUND: readonly DispositionCopy[] = [
  {
    value: 'DELIVER',
    label: 'Deliver what it was told',
    detail:
      'Everything held for it while it was paused arrives on its next read, oldest first. It catches up on the conversation it missed.',
  },
  {
    value: 'DISCARD',
    label: 'Drop what it was told',
    detail:
      'The held backlog is discarded and it starts from now. Nothing is deleted — the rows stay in the zone history, marked discarded.',
  },
]

const OUTBOUND: readonly DispositionCopy[] = [
  {
    value: 'DELIVER',
    label: 'Deliver what it said',
    detail:
      'Everything it sent while paused is released to its recipients now. If you paused it for what it was saying, this un-does the pause after the fact.',
  },
  {
    value: 'DISCARD',
    label: 'Drop what it said',
    detail:
      'Nothing it sent while paused ever reaches anybody. The rows stay in the zone history, marked discarded, so the record of what it tried to send survives.',
  },
]

function DispositionChoice({
  legend,
  hint,
  options,
  value,
  onChange,
  disabled,
}: {
  legend: string
  hint: string
  options: readonly DispositionCopy[]
  value: HeldMessageDisposition | undefined
  onChange: (next: HeldMessageDisposition) => void
  disabled: boolean
}) {
  return (
    <fieldset className="space-y-2.5" disabled={disabled}>
      <legend className="text-sm font-medium text-foreground">{legend}</legend>
      <p className="text-xs text-muted-foreground">{hint}</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => {
          const selected = value === option.value
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                onChange(option.value)
              }}
              className={cn(
                'rounded-lg border px-3.5 py-3 text-left transition-colors',
                'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                'disabled:cursor-not-allowed disabled:opacity-60',
                selected
                  ? 'border-primary bg-[color-mix(in_oklch,var(--primary)_10%,transparent)]'
                  : 'border-border bg-[var(--surface)] hover:border-[color-mix(in_oklch,var(--primary)_40%,var(--border))]',
              )}
            >
              <span className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className={cn(
                    'size-3.5 shrink-0 rounded-full border',
                    selected ? 'border-primary bg-primary' : 'border-muted-foreground/60',
                  )}
                />
                <span className="text-sm font-medium text-foreground">{option.label}</span>
              </span>
              <span className="mt-1.5 block text-xs text-pretty text-muted-foreground">
                {option.detail}
              </span>
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}

function releasedSummary(outcome: UnpauseResponse): string {
  if (!outcome.was_paused) return 'It was not paused, so nothing changed.'
  const parts: string[] = []
  parts.push(
    outcome.inbound === 'DELIVER'
      ? `${pluralise(outcome.inbound_released, 'message')} delivered to it`
      : `${pluralise(outcome.inbound_released, 'message')} addressed to it discarded`,
  )
  parts.push(
    outcome.outbound === 'DELIVER'
      ? `${pluralise(outcome.outbound_released, 'message')} it sent released`
      : `${pluralise(outcome.outbound_released, 'message')} it sent discarded`,
  )
  return `${parts.join(', ')}.`
}

function UnpauseDialog({
  zone,
  target,
  onClose,
}: {
  zone: ZoneSummary
  target: ConnectionSummary | undefined
  onClose: () => void
}) {
  const unpause = useUnpauseConnection()
  const [inbound, setInbound] = useState<HeldMessageDisposition | undefined>(undefined)
  const [outbound, setOutbound] = useState<HeldMessageDisposition | undefined>(undefined)
  const [outcome, setOutcome] = useState<UnpauseResponse | undefined>(undefined)

  function close() {
    setInbound(undefined)
    setOutbound(undefined)
    setOutcome(undefined)
    unpause.reset()
    onClose()
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (target === undefined || inbound === undefined || outbound === undefined) return
    unpause.mutate(
      { zoneId: zone.id, id: target.id, body: { inbound, outbound } },
      { onSuccess: setOutcome },
    )
  }

  const settled = outcome !== undefined

  return (
    <Dialog
      open={target !== undefined}
      onOpenChange={(next) => {
        if (!next) close()
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              Lift the pause on{' '}
              <span className="font-mono">{target?.name ?? 'this connection'}</span>
            </DialogTitle>
            <DialogDescription>
              Two separate decisions, and neither one implies the other. While the agent was
              paused, messages piled up on both sides: some addressed to it, some sent by it.
              Choose what happens to each.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-5">
            <DispositionChoice
              legend="Messages addressed to it"
              hint="What other connections said to it while it was not listening."
              options={INBOUND}
              value={inbound}
              onChange={setInbound}
              disabled={unpause.isPending || settled}
            />
            <DispositionChoice
              legend="Messages it sent"
              hint="What it said while nothing it said was going anywhere. This is the one to think about if you paused it for what it was saying."
              options={OUTBOUND}
              value={outbound}
              onChange={setOutbound}
              disabled={unpause.isPending || settled}
            />

            {unpause.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Could not lift the pause</AlertTitle>
                <AlertDescription><ApiErrorText error={unpause.error} /></AlertDescription>
              </Alert>
            ) : null}

            {outcome === undefined ? null : (
              <Alert variant="info">
                <AlertTitle>Pause lifted</AlertTitle>
                <AlertDescription>{releasedSummary(outcome)}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            {settled ? (
              <Button type="button" onClick={close}>
                Done
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={close}
                  disabled={unpause.isPending}
                >
                  Keep it paused
                </Button>
                <Button
                  type="submit"
                  disabled={
                    unpause.isPending || inbound === undefined || outbound === undefined
                  }
                >
                  {unpause.isPending ? 'Lifting…' : 'Lift the pause'}
                </Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

function ConnectionsView({ zone }: { zone: ZoneSummary }) {
  const me = useMe()
  const isAdmin = me.data?.role === 'ADMIN'
  const isZoneAdmin = useIsZoneAdministrator(zone)
  const readOnly = useReadOnly()
  const connections = useConnections(zone.id)
  const users = useUsers(isAdmin)
  const disconnect = useDisconnectConnection()
  const release = useReleaseConnection()
  const now = useTicker(CONNECTIONS_POLL_MS)
  const [pendingTarget, setPendingTarget] = useState<ConnectionSummary | undefined>(undefined)
  const [releasing, setReleasing] = useState<ConnectionSummary | undefined>(undefined)
  // The last release's answer, kept on the page: the dialog can only promise `name#N`, and the
  // real N is the one thing worth reading back.
  const [released, setReleased] = useState<
    { name: string; outcome: ReleaseResponse } | undefined
  >(undefined)
  const reissue = useReissueConnection()
  const [reissuing, setReissuing] = useState<ConnectionSummary | undefined>(undefined)
  // The one showing of a reissued id. Component state only — never a query cache — and dropped the
  // moment the dialog closes: the server keeps a digest, so this is the only copy that exists.
  const [reissued, setReissued] = useState<ReissueResponse | undefined>(undefined)
  const [pausing, setPausing] = useState<ConnectionSummary | undefined>(undefined)
  const [unpausing, setUnpausing] = useState<ConnectionSummary | undefined>(undefined)

  const rows = connections.data ?? []
  const connected = rows.filter((row) => row.state === 'CONNECTED')
  const humans = connected.filter((row) => row.type === 'human').length
  const agents = connected.filter((row) => row.type === 'agent').length
  const paused = rows.filter((row) => row.paused).length
  // Reserved identities that are not here: the name and the queue are kept (WI3).
  const offline = rows.length - connected.length

  const byName = new Map(rows.map((row) => [row.id, row.name]))
  const userNames = new Map((users.data ?? []).map((user) => [user.id, user.name]))
  // A plain member sees the column too when they own a row they may reissue.
  const showActions = !readOnly && (isAdmin || isZoneAdmin || rows.some((row) => row.can_reissue))

  return (
    <>
      <PageHeader
        eyebrow="Connections"
        title={`Connections in ${zone.name}`}
        description="Everything present in this zone — agents that called connect() over MCP and humans driving the console — plus every agent identity that is offline but still holds its name. Both kinds are first-class: a human has a name, appears in list(), and can be addressed by say(); an offline agent keeps its queue and reads it when it returns."
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void connections.refetch()
            }}
            disabled={connections.isFetching}
          >
            <RefreshCwIcon
              className={cn('size-4', connections.isFetching ? 'animate-spin' : undefined)}
            />
            Refresh
          </Button>
        }
      />

      <PageBody>
        <PausedZoneNotice zone={zone} />

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground">
              {connections.isLoading
                ? 'Loading…'
                : `${pluralise(connected.length, 'connection')} present`}
            </p>
            {connections.isLoading ? null : (
              <>
                <Badge variant="outline" className="font-mono tabular">
                  {agents} agent
                </Badge>
                <Badge variant="outline" className="font-mono tabular">
                  {humans} human
                </Badge>
                {offline === 0 ? null : (
                  <Badge variant="secondary" className="font-mono tabular">
                    {offline} offline
                  </Badge>
                )}
                {paused === 0 ? null : (
                  <Badge variant="destructive" className="font-mono tabular">
                    {paused} paused
                  </Badge>
                )}
              </>
            )}
          </div>
          <LiveIndicator active={connections.isFetching} />
        </div>

        {disconnect.isError ? (
          <Alert variant="destructive">
            <AlertTitle>Could not disconnect that connection</AlertTitle>
            <AlertDescription>{errorMessage(disconnect.error)}</AlertDescription>
          </Alert>
        ) : null}

        {released === undefined ? null : (
          <Alert variant="info">
            <AlertTitle>
              Released <span className="font-mono">{released.name}</span>
            </AlertTitle>
            <AlertDescription>
              The name is free for someone else. Its history is kept as{' '}
              <span className="font-mono">{released.outcome.history_name}</span>; its unread
              messages were discarded.
            </AlertDescription>
          </Alert>
        )}

        {connections.isError ? (
          <ErrorState
            error={connections.error}
            title="Could not load connections"
            onRetry={() => {
              void connections.refetch()
            }}
          />
        ) : connections.isLoading ? (
          <Card className="overflow-hidden">
            <TableSkeleton rows={4} columns={5} />
          </Card>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={RadioIcon}
            title="Nothing has connected to this zone yet"
            description={
              <>
                Point a client at this zone&apos;s MCP URL and have it call{' '}
                <code className="font-mono text-foreground">connect(&quot;name&quot;)</code>. Or
                join the zone yourself from the Messages page — you will show up here as a{' '}
                <code className="font-mono text-foreground">human</code>.
              </>
            }
          />
        ) : (
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Since</TableHead>
                  <TableHead>Activity</TableHead>
                  <TableHead>Waits</TableHead>
                  {showActions ? <TableHead className="text-right">Actions</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((connection) => {
                  const isConnected = connection.state === 'CONNECTED'
                  const incarnation = incarnationTag(connection.incarnation)
                  return (
                    <TableRow key={connection.id}>
                      <TableCell>
                        <span className="font-mono text-sm font-medium text-foreground">
                          {connection.name}
                        </span>
                        <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
                          {connection.id}
                        </span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {claimedLabel(connection.claimed_at, now)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <ConnectionTypeBadge type={connection.type} />
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1.5">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <ConnectionStateBadge state={connection.state} />
                            {connection.paused ? <PausedBadge /> : null}
                            {incarnation === undefined ? null : (
                              <Badge
                                variant="outline"
                                className="font-mono text-[0.6875rem]"
                                title={incarnation.title}
                              >
                                {incarnation.label}
                              </Badge>
                            )}
                          </div>
                          {connection.paused ? (
                            <span
                              className="block max-w-[22rem] text-xs text-pretty text-muted-foreground"
                              title={
                                connection.paused_at === null
                                  ? undefined
                                  : formatAbsolute(connection.paused_at)
                              }
                            >
                              Paused by{' '}
                              <span className="font-medium text-foreground">
                                {actorLabel(connection, byName, userNames, me.data?.user.id)}
                              </span>
                              {connection.paused_at === null
                                ? ''
                                : ` ${formatRelative(connection.paused_at, now)}`}
                              {connection.pause_reason === null ? null : (
                                <>
                                  {' — '}
                                  <span className="text-foreground">
                                    &ldquo;{connection.pause_reason}&rdquo;
                                  </span>
                                </>
                              )}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell
                        className="text-sm whitespace-nowrap text-muted-foreground"
                        title={formatAbsolute(connection.created_at)}
                      >
                        {formatRelative(connection.created_at, now)}
                      </TableCell>
                      <TableCell
                        className="text-sm whitespace-nowrap text-muted-foreground"
                        title={formatAbsolute(
                          isConnected
                            ? connection.last_activity_at
                            : (connection.disconnected_at ?? connection.last_activity_at),
                        )}
                      >
                        {isConnected ? (
                          <span className="font-mono tabular">
                            idle {formatDurationSince(connection.last_activity_at, now)}
                          </span>
                        ) : (
                          <>
                            last seen{' '}
                            {formatRelative(
                              connection.disconnected_at ?? connection.last_activity_at,
                              now,
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                        <CountersCell counters={connection.counters} />
                      </TableCell>
                      {showActions ? (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {isZoneAdmin ? (
                              connection.paused ? (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setUnpausing(connection)
                                  }}
                                >
                                  <PlayIcon className="size-3.5" />
                                  Unpause
                                </Button>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={!isConnected}
                                  onClick={() => {
                                    setPausing(connection)
                                  }}
                                  className="text-muted-foreground hover:text-destructive"
                                >
                                  <PauseIcon className="size-3.5" />
                                  Pause
                                </Button>
                              )
                            ) : null}
                            {/* Reissue: the row's owner or the zone administrator, as the server
                                says per row in `can_reissue`. For an agent that lost its id —
                                the alternative, release, discards its queue. */}
                            {connection.can_reissue ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={reissue.isPending}
                                onClick={() => {
                                  setReissuing(connection)
                                }}
                                className="text-muted-foreground"
                              >
                                <KeyRoundIcon className="size-3.5" />
                                Reissue id
                              </Button>
                            ) : null}
                            {/* WI9: release is the zone administrator's (same gate as pause) and
                                only for identities — a human row is a presence, not a reserved
                                name, and the server answers 404 for it. Offered online or offline:
                                releasing a live agent force-disconnects it first. */}
                            {isZoneAdmin && connection.type !== 'human' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={release.isPending}
                                onClick={() => {
                                  setReleasing(connection)
                                }}
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <UserMinusIcon className="size-3.5" />
                                Release
                              </Button>
                            ) : null}
                            {isAdmin ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={!isConnected || disconnect.isPending}
                                onClick={() => {
                                  setPendingTarget(connection)
                                }}
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <PlugZapIcon className="size-3.5" />
                                Disconnect
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        {isZoneAdmin ? (
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <ShieldIcon className="mt-0.5 size-3.5 shrink-0" />
            <span>
              You administer this zone, so you can pause, unpause and release here. Release is
              the destructive one: it frees an agent&apos;s name and drops its unread queue, where
              a disconnect only takes it offline. A zone&apos;s police connection can pause too,
              from inside the zone — configure that on the{' '}
              <span className="text-foreground">Police</span> page.
            </span>
          </p>
        ) : null}
      </PageBody>

      <PauseDialog
        zone={zone}
        target={pausing}
        onClose={() => {
          setPausing(undefined)
        }}
      />
      <UnpauseDialog
        zone={zone}
        target={unpausing}
        onClose={() => {
          setUnpausing(undefined)
        }}
      />

      <AlertDialog
        open={pendingTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingTarget(undefined)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Force-disconnect{' '}
              <span className="font-mono">{pendingTarget?.name ?? 'this connection'}</span>?
            </AlertDialogTitle>
            <AlertDialogDescription>
              It is taken offline now, and that is all. It keeps its name, its queue and its
              channel memberships: anything addressed to it waits, and it comes back as itself by
              calling <code className="font-mono">connect</code> again with its connection id.
              Nothing is discarded. To free the name and drop the queue, release it instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disconnect.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive' }))}
              disabled={disconnect.isPending}
              onClick={(event) => {
                event.preventDefault()
                const target = pendingTarget
                if (target === undefined) return
                disconnect.mutate(
                  { zoneId: zone.id, id: target.id },
                  {
                    onSuccess: () => {
                      setPendingTarget(undefined)
                    },
                  },
                )
              }}
            >
              {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reissue, step one: say what happens to the old id before anything happens to it. */}
      <AlertDialog
        open={reissuing !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setReissuing(undefined)
            reissue.reset()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Reissue the connection id of{' '}
              <span className="font-mono">{reissuing?.name ?? 'this identity'}</span>?
            </AlertDialogTitle>
            <AlertDialogDescription>
              For an agent that lost its id. You get a new id, shown once. The current id and
              watch URL stop working now. The name, the unread messages and the channels stay as
              they are. A session that is already connected keeps working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {reissue.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not reissue that id</AlertTitle>
              <AlertDescription><ApiErrorText error={reissue.error} /></AlertDescription>
            </Alert>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reissue.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={reissue.isPending}
              onClick={(event) => {
                event.preventDefault()
                const target = reissuing
                if (target === undefined) return
                reissue.mutate(
                  { zoneId: zone.id, id: target.id },
                  {
                    onSuccess: (outcome) => {
                      setReissuing(undefined)
                      setReissued(outcome)
                      reissue.reset()
                    },
                  },
                )
              }}
            >
              {reissue.isPending ? 'Reissuing…' : 'Reissue id'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reissue, step two: the one showing. Closing it drops the id from memory. */}
      <AlertDialog
        open={reissued !== undefined}
        onOpenChange={(open) => {
          if (!open) setReissued(undefined)
        }}
      >
        <AlertDialogContent className="sm:max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              New connection id for <span className="font-mono">{reissued?.name ?? ''}</span>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Copy it now. It will not be shown again. Treat it like a password: give it to the
              agent, not to a chat or a message.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {reissued === undefined ? null : (
            // min-w-0: the dialog is a grid, and a grid child defaults to min-width:auto, which lets
            // a long id or watch URL push past the dialog instead of truncating with an ellipsis.
            // The full value is still what the copy button copies, and the title attribute shows it.
            <div className="min-w-0 space-y-3">
              <CopyableValue value={reissued.connection} label="Copy connection id" />
              <p className="text-sm text-muted-foreground">
                The agent reconnects with{' '}
                <span className="font-mono text-foreground">
                  connect(connection=&quot;…&quot;)
                </span>
                . It has to run under this identity's owner's token.
              </p>
              {reissued.watch_url === null ? null : (
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">New watch URL</p>
                  <CopyableValue value={reissued.watch_url} label="Copy watch URL" muted />
                </div>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogAction>Done</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* WI9. The §19 shape: the title asks, the body states every consequence, the action is
          destructive. `name#N` is literal — N is decided by the server and shown after. */}
      <AlertDialog
        open={releasing !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setReleasing(undefined)
            release.reset()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Release <span className="font-mono">{releasing?.name ?? 'this identity'}</span>?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This frees the name <span className="font-mono">{releasing?.name ?? ''}</span> for
              someone else. Its unread messages are discarded. Its history is kept as{' '}
              <span className="font-mono">{releasing?.name ?? ''}#N</span>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {release.isError ? (
            <Alert variant="destructive">
              <AlertTitle>Could not release that identity</AlertTitle>
              <AlertDescription>{errorMessage(release.error)}</AlertDescription>
            </Alert>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={release.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={cn(buttonVariants({ variant: 'destructive' }))}
              disabled={release.isPending}
              onClick={(event) => {
                event.preventDefault()
                const target = releasing
                if (target === undefined) return
                release.mutate(
                  { zoneId: zone.id, id: target.id },
                  {
                    onSuccess: (outcome) => {
                      setReleased({ name: target.name, outcome })
                      setReleasing(undefined)
                    },
                  },
                )
              }}
            >
              {release.isPending ? 'Releasing…' : 'Release'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export function ConnectionsPage() {
  return <RequireZone>{(zone) => <ConnectionsView zone={zone} />}</RequireZone>
}
